import * as React from 'react';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { SubmissionStatus } from '@clawhire/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { toast } from '@/components/ui/toaster';
import { invalidateAfterSubmissionChange, SUBMISSIONS_ENTITY } from '@/pages/submissions/api';
import { statusChangeNeedsDialog } from '@/pages/submissions/helpers';
import { StatusChangeDialog, type StatusChangeRequest } from '@/pages/submissions/StatusChangeDialog';
import type { StatusChangeInput, StatusChangeResult } from '@/pages/submissions/types';

async function movePositionToSourcing(positionId: string, queryClient: QueryClient): Promise<void> {
  try {
    await api.post(`/positions/${encodeURIComponent(positionId)}/stage`, { stage: 'SOURCING' });
    toast.success('Position moved back to Sourcing');
    await queryClient.invalidateQueries({ queryKey: ['positions'] });
    await queryClient.invalidateQueries({ queryKey: [SUBMISSIONS_ENTITY] });
  } catch (e) {
    toast.error(e instanceof Error ? e.message : 'Could not move the position');
  }
}

/** Toasts for the `hints[]` and `warnings[]` a transition returns (spec §6.2). */
export function showTransitionFeedback(result: StatusChangeResult, queryClient: QueryClient): void {
  for (const w of result.warnings) toast.warning(w.message);
  for (const hint of result.hints) {
    if (/sourcing/i.test(hint)) {
      const positionId = result.submission.position.id;
      toast.info(hint, {
        duration: 12_000,
        action: {
          label: 'Move to Sourcing',
          onClick: () => void movePositionToSourcing(positionId, queryClient),
        },
      });
    } else {
      toast.info(hint);
    }
  }
}

export interface UseSubmissionStatusChangeResult {
  /** Start a transition: opens the dialog when a reason/date is needed, otherwise posts directly. */
  request: (req: StatusChangeRequest) => void;
  /** Render this once on the page (the outcome-reason / sent-on dialog). */
  dialog: React.ReactNode;
  isPending: boolean;
  /** Id of the submission whose transition is in flight. */
  pendingId: string | null;
}

/**
 * `POST /submissions/:id/status` with the dialog flow around it. Used by the board (drag & drop)
 * and by the detail page stepper.
 */
export function useSubmissionStatusChange(
  options: {
    onSuccess?: (result: StatusChangeResult) => void;
  } = {},
): UseSubmissionStatusChangeResult {
  const queryClient = useQueryClient();
  const [pending, setPending] = React.useState<StatusChangeRequest | null>(null);
  const { onSuccess } = options;

  const mutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: StatusChangeInput }) =>
      api.post<StatusChangeResult>(`/submissions/${encodeURIComponent(id)}/status`, input),
    onSuccess: async (result, variables) => {
      queryClient.setQueryData(queryKeys.detail(SUBMISSIONS_ENTITY, variables.id), result.submission);
      toast.success(`Status → ${SubmissionStatus.label(result.submission.status)}`);
      showTransitionFeedback(result, queryClient);
      await invalidateAfterSubmissionChange(queryClient, result.submission.id);
      onSuccess?.(result);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const request = React.useCallback(
    (req: StatusChangeRequest) => {
      if (req.to === req.from) return;
      if (statusChangeNeedsDialog(req.to)) setPending(req);
      else mutation.mutate({ id: req.id, input: { status: req.to } });
    },
    [mutation],
  );

  const dialog = (
    <StatusChangeDialog
      request={pending}
      onCancel={() => setPending(null)}
      onConfirm={async (input) => {
        if (!pending) return;
        await mutation.mutateAsync({ id: pending.id, input });
        setPending(null);
      }}
    />
  );

  return {
    request,
    dialog,
    isPending: mutation.isPending,
    pendingId: mutation.isPending ? (mutation.variables?.id ?? null) : null,
  };
}
